import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, formatViews, formatRelative, typeLabel } from "@/utils/format";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareBar from "@/pages/detail/components/ShareBar";
import ImageLightbox from "@/pages/detail/components/ImageLightbox";

export default function DetailPage() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);
  const [lightbox, setLightbox] = useState<string | null>(null);

  usePageMeta({
    title: article ? `${article.title} - 吃瓜网站` : "资讯未找到 - 吃瓜网站",
    description: article
      ? `${article.summary} 吃瓜网站为你带来${article.categoryName}的最新资讯与深度解读。`
      : "该资讯暂不存在，返回吃瓜网站首页查看最新娱乐内容。",
    keywords: article ? `${article.title},${article.tags.join(",")},${article.categoryName},吃瓜网站` : "吃瓜网站,娱乐资讯",
  });

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <i className="ri-file-unknow-line text-6xl text-foreground-300" />
        <h2 className="mt-4 text-xl font-bold text-foreground-900">资讯不存在</h2>
        <p className="mt-2 text-sm text-foreground-500">这篇文章可能已被移除，去看看其他娱乐内容吧。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const related = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 5);
  const fillRelated = articles.filter((a) => a.id !== article.id && !related.some((r) => r.id === a.id));

  const relatedList = [...related, ...fillRelated].slice(0, 5);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: article.categoryName, to: `/category/${article.category}` },
          { label: "正文" },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
        <article className="lg:col-span-2 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-3">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-500 hover:text-background-50 whitespace-nowrap transition-colors"
            >
              <i className="ri-price-tag-3-line" />
              {article.categoryName}
            </Link>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">
              <i className="ri-play-circle-line" />
              {typeLabel(article.type)}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 leading-snug">{article.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {formatDate(article.publishTime)}（{formatRelative(article.publishTime)}）
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(article.views)} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-3-line" />
              {formatViews(article.likes)} 点赞
            </span>
          </div>

          <figure className="mt-5">
            <button
              type="button"
              onClick={() => setLightbox(article.cover)}
              className="block w-full rounded-lg overflow-hidden cursor-zoom-in group relative"
              aria-label="查看大图"
            >
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 吃瓜网站`}
                className="w-full h-56 md:h-96 object-cover object-top group-hover:opacity-95 transition-opacity"
              />
              <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/50 text-white text-xs">
                <i className="ri-zoom-in-line" />
                点击放大
              </span>
            </button>
            <figcaption className="mt-2 text-center text-xs text-foreground-400">图为相关资讯配图（演示内容）</figcaption>
          </figure>

          <ShareBar id={article.id} title={article.title} />

          <div className="prose-like space-y-4 text-[15px] md:text-base leading-8 text-foreground-700">
            {article.content.map((p, i) => (
              <div key={i}>
                <p>{p}</p>
                {i === 1 && article.gallery[0] && (
                  <button
                    type="button"
                    onClick={() => setLightbox(article.gallery[0])}
                    className="block w-full my-5 rounded-lg overflow-hidden cursor-zoom-in"
                    aria-label="查看大图"
                  >
                    <img
                      src={article.gallery[0]}
                      alt={`${article.title}配图`}
                      title={`${article.title} - 吃瓜网站`}
                      className="w-full h-56 md:h-80 object-cover object-top"
                    />
                  </button>
                )}
              </div>
            ))}
            {article.type === "video" && (
              <div className="rounded-lg bg-foreground-950 text-background-50 p-6 flex flex-col items-center gap-2 my-6">
                <i className="ri-play-circle-line text-5xl text-primary-400" />
                <p className="text-sm">该资讯包含视频内容</p>
                <span className="text-xs text-background-300">视频播放功能开发中，敬请期待</span>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">相关标签：</span>
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="px-3 py-1 rounded-full bg-background-100 text-xs md:text-sm text-foreground-600 hover:bg-secondary-500 hover:text-background-50 whitespace-nowrap transition-colors"
              >
                #{t}
              </Link>
            ))}
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h3 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
              <i className="ri-chat-3-line text-primary-500" />
              评论区
            </h3>
            <div className="rounded-md bg-background-100 border border-dashed border-background-300 py-8 text-center">
              <i className="ri-emotion-happy-line text-4xl text-foreground-300" />
              <p className="mt-2 text-sm text-foreground-500">评论功能即将上线，敬请期待</p>
            </div>
          </div>
        </article>

        <aside className="lg:col-span-1 space-y-6">
          {category && (
            <div className="rounded-lg border border-background-200 bg-primary-50 p-5">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl`} />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">所属分类</p>
                  <p className="text-base font-bold text-foreground-900">{category.name}</p>
                </div>
              </div>
              <p className="mt-3 text-xs md:text-sm text-foreground-600 leading-relaxed">{category.desc}</p>
              <Link
                to={`/category/${category.slug}`}
                className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 font-medium hover:gap-2 transition-all"
              >
                查看该分类全部资讯
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          )}

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
              <i className="ri-links-line text-accent-500" />
              相关推荐
            </h3>
            <div className="space-y-3">
              {relatedList.map((a) => (
                <Link key={a.id} to={`/detail/${a.id}`} className="group flex gap-3">
                  <div className="w-24 h-16 shrink-0 rounded-md overflow-hidden">
                    <img
                      src={a.cover}
                      alt={a.title}
                      title={`${a.title} - 吃瓜网站`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {a.title}
                    </p>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-eye-line" />
                      {formatViews(a.views)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {lightbox && <ImageLightbox src={lightbox} alt={article.title} onClose={() => setLightbox(null)} />}
    </div>
  );
}